import type { MrsTableRow } from '../types';

/** One MRS line the picklist leaves out, with what it could still have taken (the 09-18 planning basis). */
export interface CoverageLeftLine {
  itemCode: string;
  itemName: string;
  quantity: number;
  unitOfMeasure: string;
}

/** PT-7 E3 — how much of one MRS the picklist being created takes. */
export interface CoverageBlock {
  mrsId: number;
  mrsNumber: string;
  requestorFullName: string;
  /** Selectable lines this picklist carries: picked in this request, plus (Add Items) those already on it. */
  inLines: number;
  /** The MRS's selectable lines, as the selection table shows them. */
  totalLines: number;
  left: CoverageLeftLine[];
}

/** The selection table's own rule (`ItemSelectionPanel.isSelectableItem`): a real, stockable NetSuite item. */
const isSelectable = (row: MrsTableRow['subRows'][number]) => !row.isNonCatalog && row.netsuiteItemId !== 0;

/**
 * PT-7 E3 — the Create confirm's MRS coverage block: for every MRS in the selection, how many of its lines this
 * picklist takes and which it leaves, so an unticked line cannot go unnoticed.
 *
 * `pickByLineKey` maps `PlannedLine.key` (`${mrsId}-${itemId}`) to the quantity that will be sent, over the plans
 * that will actually be sent (out-of-stock plans are already dropped). Its insertion order gives the blocks'
 * order: first-seen over the review's plans, then their lines. An MRS is in the selection when at least one of
 * its lines has `pick > 0`.
 *
 * A line the target picklist already covers in full never reaches the table (the backend filters it), so on
 * Add Items it counts in neither figure; one it covers in part counts as in (`alreadyOnPicklist > 0`).
 */
export function creationCoverage(
  rows: readonly MrsTableRow[],
  pickByLineKey: ReadonlyMap<string, number>,
): CoverageBlock[] {
  const order: number[] = [];
  for (const [key, pick] of pickByLineKey) {
    if (!(pick > 0)) continue;
    const mrsId = Number(key.split('-')[0]);
    if (!order.includes(mrsId)) order.push(mrsId);
  }

  const blocks: CoverageBlock[] = [];
  for (const mrsId of order) {
    const row = rows.find((r) => r.mrsId === mrsId);
    if (!row) continue;
    const selectable = row.subRows.filter(isSelectable);
    const isIn = (line: MrsTableRow['subRows'][number]) =>
      (pickByLineKey.get(`${line.mrsId}-${line.itemId}`) ?? 0) > 0 || (line.alreadyOnPicklist ?? 0) > 0;
    blocks.push({
      mrsId,
      mrsNumber: row.mrsNumber,
      requestorFullName: row.requestorFullName,
      inLines: selectable.filter(isIn).length,
      totalLines: selectable.length,
      left: selectable
        .filter((line) => !isIn(line))
        .map((line) => ({
          itemCode: line.itemCode,
          itemName: line.itemName,
          quantity: line.availableQuantity,
          unitOfMeasure: line.unitOfMeasure,
        })),
    });
  }
  return blocks;
}
